import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { MovieCard } from "@/components/MovieCard";
import { Search as SearchIcon, X } from "lucide-react";

const Search = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    api.get("/movies").then((r) => setResults(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      setTouched(true);
      const params = q.trim() ? { q: q.trim() } : {};
      api.get("/movies", { params })
        .then((r) => setResults(r.data))
        .finally(() => setLoading(false));
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="min-h-screen bg-[#030303] pt-24 pb-20 px-4 sm:px-8 max-w-[1600px] mx-auto">
      <div className="relative max-w-2xl mb-10">
        <SearchIcon size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="search-input"
          placeholder="Search titles, genres, origins..."
          className="w-full bg-[#111111] border border-white/10 rounded-full pl-12 pr-12 py-4 text-white text-base placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#00F0FF] focus:border-transparent transition-shadow"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
            <X size={20} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#00F0FF] animate-spin" />
        </div>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="search-empty">
          <SearchIcon size={48} className="text-white/20 mb-4" />
          <p className="text-white/60">{touched && q ? `No results for "${q}"` : "Start typing to search."}</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-white/50 mb-6">{results.length} title{results.length !== 1 ? "s" : ""} found</p>
          <div className="grid gap-4 sm:gap-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }} data-testid="search-results">
            {results.map((m) => (
              <div key={m.id} className="[&>a]:!w-full">
                <MovieCard movie={m} testid={`search-card-${m.id}`} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Search;
